import Ably from 'ably';
import type { AblyConfig, ApiResponse, ChatConfig, Message, WidgetConfig } from '@/types';

import { ApiService } from './ApiService';

export class AblyService {
  private client: Ably.Realtime;
  private channel: Ably.Types.RealtimeChannelCallbacks;
  private config: AblyConfig;
  private messageCallback?: (message: Message) => void;
  private connectionCallback?: (state: string) => void;

  private apiService: ApiService;


  private baseUrl: string;
  private apiKey: string;
  private readonly DEMO_TOKEN = "Da7q7oN6pPmJdterDcjSI3Jnkrke9z63sOmGvITLVAh3bWBugr9YAYSjxTnU5Z61";

  constructor(config: AblyConfig) {
    this.config = config;
    this.initializeClient();
  }


  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const defaultHeaders = {
      'Authorization': `Bearer ${this.apiKey}`,
      'Content-Type': 'application/json'
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }


  /**
   * Envía un mensaje a través del ChatCenter
   */
  // async sendMessage(sessionId: string, message: Message): Promise<void> {
  //   const response = await this.request('/chat/message', {
  //     method: 'POST',
  //     body: JSON.stringify({
  //       sessionId,
  //       message: {
  //         text: message.text,
  //         type: message.type,
  //         timestamp: message.timestamp.toISOString(),
  //         clientId: message.id
  //       }
  //     })
  //   });

  //   if (!response.success) {
  //     throw new Error(response.error || 'Error enviando mensaje');
  //   }
  // }

  // askdjfkjsdlkfjklsdj----------------------------

  private initializeClient(): void {
    try {
      const clientOptions: Ably.Types.ClientOptions = {
        clientId: this.config.clientId,
        key: this.config.apiKey
      };

      this.client = new Ably.Realtime(clientOptions);

      // Usar el channelName del config (ej: "webchat:visitor_123")
      this.channel = this.client.channels.get(this.config.channelName);
      this.setupEventListeners();

    } catch (error) {
      console.error('❌ Error inicializando Ably client:', error);
      throw new Error('Failed to initialize Ably client');
    }
  }


  private setupEventListeners(): void {

    // Suscribirse al evento específico de respuesta de mensajes web
    this.channel.subscribe('message-web-response', (ablyMessage) => {
      if (this.messageCallback && ablyMessage.data) {
        try {
          // Procesar según la estructura real que me mostraste

          const messageData = ablyMessage.data;


          //TYPE BUTTON
          if (messageData.Of === 'Bot' && messageData.Type === 'BUTTON') {

            const header = messageData.Phrase?.header.type === 'text'
              ? messageData.Phrase.header.text
              : messageData.Phrase?.header || '';

            const message: Message = {
              id: messageData.ID || Date.now().toString(),
              text: messageData.Phrase?.body || '',
              sender: 'bot',
              type: 'button',
              timestamp: new Date(messageData.Date || ablyMessage.timestamp || Date.now()),
              senderType: 'Bot',
              botData: {
                header: header,
                body: messageData.Phrase?.body || '',
                footer: messageData.Phrase?.footer || '',
                buttons: messageData.Phrase?.buttons || []
              }
            };

            this.messageCallback(message);
            return;
          }

          // TYPE LIST
          if (messageData.Of === 'Bot' && messageData.Type === 'LIST') {

            const message: Message = {
              id: messageData.ID || Date.now().toString(),
              text: messageData.Phrase?.body || '',
              sender: 'bot',
              type: 'list',
              timestamp: new Date(messageData.Date || ablyMessage.timestamp || Date.now()),
              senderType: 'Bot',
              botData: {
                header: messageData.Phrase?.header || '',
                body: messageData.Phrase?.body || '',
                footer: messageData.Phrase?.footer || '',
                buttons: messageData.Phrase?.buttons || [],
                buttonText: messageData.Phrase?.buttonText || ''
              }
            };
            this.messageCallback(message);
            return;
          }

          // Determinar el tipo de mensaje
          let messageType: 'text' | 'image' | 'file' | 'system' = 'text';
          let messageText = '';
          let fileUrl = '';
          let caption = '';
          let fileName = '';
          const senderType = messageData.Of === 'Bot' ? 'Bot' :
            messageData.Of === 'Operador' ? 'Operador' :
              undefined;


          // const messageText = messageData?.Phrase ||
          //   messageData.message?.text ||
          //   messageData.text ||
          //   'Mensaje sin contenido';
          // Procesar según el Type del mensaje
          if (messageData.Type === 'IMAGE') {
            messageType = 'image';
            fileUrl = messageData.Phrase || messageData.fileUrl || messageData.url || '';; // URL de la imagen
            caption = messageData.Caption || '';
            messageText = caption || 'Imagen';

            // Extraer nombre del archivo de la URL
            try {
              fileName = fileUrl.split('/').pop() || 'imagen.jpg';
            } catch (e) {
              fileName = 'imagen.jpg';
            }

          } else if (messageData.Type === 'DOCUMENT') {
            messageType = 'file';
            fileUrl = messageData.Phrase; // URL del documento
            caption = messageData.Caption || '';
            messageText = caption || 'Documento';

            // Extraer nombre del archivo de la URL
            try {
              // fileName = fileUrl.split('/').pop() || 'documento.pdf';
              fileName = 'documento.pdf';
            } catch (e) {
              fileName = 'documento.pdf';
            }


          } else {
            // Mensaje de texto normal
            messageText = messageData.data?.message?.text ||
              messageData.message?.text ||
              messageData.Phrase ||
              messageData.text ||
              'Mensaje sin contenido';
          }

          // Extraer información del agente si está disponible
          const agentName = messageData.conversation?.agent?.name || 'Agente';



          const TYPE_SENDER = messageData.Of === 'Bot' ? 'bot' : 'agent';

          // Crear objeto Message
          const message: Message = {
            id: messageData.ID || messageData.data?.messageUuid || Date.now().toString(),
            text: messageText,
            sender: TYPE_SENDER,
            type: messageType,
            timestamp: new Date(messageData.Date || ablyMessage.timestamp || Date.now()),
            senderType: senderType,
            ...(fileUrl && { fileUrl }),
            ...(fileName && { fileName }),
            ...(caption && { caption })
          };



          // this.playNotificationSound();
          this.messageCallback(message);

        } catch (error) {
          console.error('❌ Error procesando mensaje de Ably:', error);
          // console.error('Estructura recibida:', ablyMessage.data);
        }
      }
    });

    this.channel.subscribe('web-conversation-transfer', (ablyMessage) => {

      if (this.messageCallback && ablyMessage.data) {
        try {
          const transferData = ablyMessage.data;

          const message: Message = {
            id: transferData.ID || Date.now().toString(),
            text: `Su conversación se transfirió a ${transferData.toAgent || 'Agente'}`,
            sender: 'system',
            type: 'transfer',
            timestamp: new Date(transferData.Date || ablyMessage.timestamp || Date.now()),
            agentName: transferData.toAgent,
            // description: transferData.Description
          };

          this.messageCallback(message);

        } catch (error) {
          console.error('❌ Error procesando transferencia:', error);
        }
      }
    });

    this.channel.subscribe('web-conversation-finished', (ablyMessage) => {
      if (this.messageCallback && ablyMessage.data) {
        try {
          const finishedData = ablyMessage.data;

          const message: Message = {
            id: finishedData.ID || Date.now().toString(),
            // text: `Su conversación fue finalizada por el agente: ${finishedData.agentName || 'Agente'}`,
            text: `Su conversación fue finalizada`,
            sender: 'system',
            type: 'finished',
            timestamp: new Date(finishedData.Date || ablyMessage.timestamp || Date.now()),
            agentName: finishedData.agentName,
            description: finishedData.Description
          };


          this.messageCallback(message);

        } catch (error) {
          console.error('❌ Error procesando finalización:', error);
        }
      }
    });

    // Listener genérico (existente)
    this.channel.subscribe((ablyMessage) => {
      const knownEvents = ['message-web-response', 'web-conversation-transfer', 'web-conversation-finished'];
      if (!knownEvents.includes(ablyMessage.name)) {

      }
    });



    // También suscribirse a cualquier otro evento por si acaso
    // this.channel.subscribe((ablyMessage) => {
    // });

    // Listeners de conexión (sin cambios)
    this.client.connection.on('connected', () => {
      if (this.connectionCallback) {
        this.connectionCallback('connected');
      }
      this.enterPresence();
    });

    this.client.connection.on('disconnected', () => {
      if (this.connectionCallback) {
        this.connectionCallback('disconnected');
      }
    });

    this.client.connection.on('connecting', () => {
      if (this.connectionCallback) {
        this.connectionCallback('connecting');
      }
    });

    this.client.connection.on('failed', (error) => {
      console.error('Conexión a Ably falló:', error);
      if (this.connectionCallback) {
        this.connectionCallback('failed');
      }
    });
  }

  private enterPresence(): void {
    try {
      this.channel.presence.enter({
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        clientType: 'webchat-widget',
        visitorId: this.config.clientId
      });
    } catch (error) {
      console.error('❌ Error establishing presence:', error);
    }
  }



  public onMessage(callback: (message: Message) => void): void {
    this.messageCallback = callback;
  }

  public onConnectionStateChange(callback: (state: string) => void): void {
    this.connectionCallback = callback;
  }

  public getConnectionState(): string {
    return this.client.connection.state;
  }

  public isConnected(): boolean {
    return this.client.connection.state === 'connected';
  }

  public async disconnect(): Promise<void> {
    try {
      await this.channel.presence.leave();
      this.client.close();
    } catch (error) {
      console.error('Error desconectando:', error);
    }
  }

  public getChannelState(): any {
    return {
      channelName: this.config.channelName,
      connectionState: this.client.connection.state,
      channelState: this.channel.state
    };
  }

  // Info del canal (para debugging)
  public getChannelInfo(): any {
    return {
      channelName: this.config.channelName,
      connectionState: this.client.connection.state,
      channelState: this.channel.state,
      clientId: this.config.clientId
    };
  }

  //sound for new message
  // private playNotificationSound(): void {
  //   const audio = new Audio('/src/assets/webChatNotification.mp3'); // Ruta del sonido
  //   audio.volume = 0.6; // volumen de 0.0 a 1.0
  //   audio.play().catch((error) => {
  //     console.warn('🔇 No se pudo reproducir el sonido automáticamente:', error);
  //   });
  // }
}
